import {Component, computed, inject, signal, input, output, viewChild} from '@angular/core';
import {rxResource, takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Button} from 'primeng/button';
import {OfficeMultiSelectComponent} from '../../components/office-multi-select/office-multi-select.component';
import {ModelFieldAutocompleteComponent} from '../../../../../../../../shared/components/inputs/model-field/model-field-autocomplete/model-field-autocomplete.component';
import {ModelFieldSelectComponent} from '../../../../../../../../shared/components/inputs/model-field/model-field-select/model-field-select.component';
import {PlannedAction, ProjectOfficeResponse} from '@frontend/shared-api';
import {ProviderData, ToolData, FunctionEntry} from '../../system-recording.types';
import {TranslocoPipe} from '@jsverse/transloco';
import {ToastService} from '@frontend/toast';
import {from, Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {SharedModule} from 'primeng/api';
import {SystemRecordingStateService} from '../../system-recording-state.service';
import {ModuleFunctionLabelComponent} from '../../../../../../../../shared/components/module-function-label/module-function-label.component';
import {ProjectOfficesProviderService} from '../../services/project-offices-provider.service';
import {ConfirmationDialogService} from '../../../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-system-recording-function-row',
  imports: [
    Button,
    TranslocoPipe,
    SharedModule,
    ModuleFunctionLabelComponent,
    OfficeMultiSelectComponent,
    ModelFieldAutocompleteComponent,
    ModelFieldSelectComponent,
  ],
  templateUrl: './system-recording-function-row.component.html',
})
export class SystemRecordingFunctionRowComponent {
  private readonly systemRecordingStateService = inject(SystemRecordingStateService);
  private readonly toastService = inject(ToastService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly projectOfficesProviderService = inject(ProjectOfficesProviderService);

  readonly functionEntry = input.required<FunctionEntry>();
  readonly moduleId = input.required<number>();
  readonly projectId = input.required<number>();
  readonly hasDuplicates = input.required<boolean>();
  readonly isReadOnly = input<boolean>(false);

  readonly functionChanged = output<void>();

  readonly providerAutocomplete =
    viewChild<ModelFieldAutocompleteComponent>('providerAutocomplete');
  readonly toolAutocomplete = viewChild<ModelFieldAutocompleteComponent>('toolAutocomplete');

  private readonly currentProviderQuery = signal('');
  private readonly currentToolQuery = signal('');

  private readonly providerSearchSubject = new Subject<string>();
  private readonly toolSearchSubject = new Subject<{query: string; providerId: number | null}>();

  protected isSyncingProvider = signal(false);

  constructor() {
    this.providerSearchSubject.pipe(debounceTime(200), takeUntilDestroyed()).subscribe((query) => {
      this.systemRecordingStateService.searchProviders(query);
    });

    this.toolSearchSubject
      .pipe(debounceTime(200), takeUntilDestroyed())
      .subscribe(({query, providerId}) => {
        this.systemRecordingStateService.searchTools(query, providerId);
      });
  }

  protected readonly PlannedAction = PlannedAction;

  private readonly officesResource = rxResource({
    params: () => ({projectId: this.projectId()}),
    stream: ({params: request}) =>
      from(this.projectOfficesProviderService.getOffices(request.projectId)),
  });

  protected readonly hasMultipleOffices = computed(
    () => (this.officesResource.value() ?? []).length > 1,
  );

  protected readonly plannedActionOptions = computed(() => {
    if (this.functionEntry().isInProjectScope) {
      return this.systemRecordingStateService.getPlannedInProjectScopeActionOptions();
    }
    return this.systemRecordingStateService.getPlannedActionOptions();
  });

  protected readonly filteredProviders = this.systemRecordingStateService.filteredProviders;
  protected readonly filteredTools = this.systemRecordingStateService.filteredTools;

  protected readonly currentFunctionEntry = computed(() => {
    const module = this.systemRecordingStateService
      .moduleData()
      .find((m) => m.id === this.moduleId());
    return module?.functions.find((f) => f.uniqueId === this.functionEntry().uniqueId);
  });

  protected readonly providerDisplayValue = computed(() => {
    const currentEntry = this.currentFunctionEntry();
    if (!currentEntry?.providerId) return null;
    return (
      this.systemRecordingStateService.providerLookupMap().get(currentEntry.providerId) ?? null
    );
  });

  protected readonly toolDisplayValue = computed(() => {
    const currentEntry = this.currentFunctionEntry();
    if (!currentEntry?.toolId || !currentEntry?.providerId) return null;
    const toolMap = this.systemRecordingStateService.toolLookupMap().get(currentEntry.providerId);
    return toolMap?.get(currentEntry.toolId) ?? null;
  });

  protected readonly isAddProviderDisabled = computed(() => {
    const query = this.currentProviderQuery().trim().toLowerCase();
    if (!query) return true;

    return this.filteredProviders().some((group) =>
      group.items?.some((provider) => provider.providerName.toLowerCase() === query),
    );
  });

  protected readonly isAddToolDisabled = computed(() => {
    const query = this.currentToolQuery().trim().toLowerCase();
    if (!query) return true;

    return this.filteredTools().some((group) =>
      group.items?.some((tool) => tool.toolName.toLowerCase() === query),
    );
  });

  onPlannedActionChange(action: PlannedAction): void {
    this.systemRecordingStateService.onPlannedActionChange(this.functionEntry().uniqueId, action);

    if (action === PlannedAction.NEW || action === PlannedAction.NO_SYSTEM) {
      this.clearProviderAndToolSelection();
    }

    this.functionChanged.emit();
  }

  onProviderChange(provider: ProviderData | null): void {
    if (provider) {
      this.systemRecordingStateService.onProviderChange(
        this.functionEntry().uniqueId,
        provider.providerId,
      );
      this.systemRecordingStateService.searchTools('', provider.providerId);
    } else {
      this.systemRecordingStateService.updateFunctionEntry(this.functionEntry().uniqueId, {
        providerId: null,
        toolId: null,
      });
    }
    if (provider && provider.tools.length === 1) {
      this.systemRecordingStateService.onToolChange(
        this.functionEntry().uniqueId,
        provider.tools[0].toolId,
        provider.providerId,
      );
    } else {
      this.clearToolSelection();
    }

    this.functionChanged.emit();
  }

  onToolChange(tool: ToolData | null): void {
    const currentEntry = this.currentFunctionEntry();
    if (tool && currentEntry?.providerId) {
      this.systemRecordingStateService.onToolChange(
        this.functionEntry().uniqueId,
        tool.toolId,
        currentEntry.providerId,
      );
    } else if (!tool) {
      this.systemRecordingStateService.updateFunctionEntry(this.functionEntry().uniqueId, {
        toolId: null,
      });
    }

    this.functionChanged.emit();
  }

  onOfficeSelectionChange(offices: ProjectOfficeResponse[]): void {
    this.systemRecordingStateService.onOfficeSelectionChange(
      this.functionEntry().uniqueId,
      offices,
    );
    this.functionChanged.emit();
  }

  onAddInstance(): void {
    this.systemRecordingStateService.duplicateFunction(
      this.moduleId(),
      this.functionEntry().uniqueId,
    );
    this.functionChanged.emit();
  }

  onRemoveInstance(): void {
    if (!this.functionEntry().backendId) {
      this.systemRecordingStateService.removeFunction(
        this.moduleId(),
        this.functionEntry().uniqueId,
      );
      this.functionChanged.emit();
      return;
    }

    this.confirmDialogService.confirm({
      headerKey: 'project.steps.systemRecording.confirmDeleteInstance.header',
      messageKey: 'project.steps.systemRecording.confirmDeleteInstance.message',
      accept: async () => {
        this.isSyncingProvider.set(true);
        try {
          await this.systemRecordingStateService.deleteFunctionInstance(
            this.moduleId(),
            this.functionEntry().backendId!,
          );
          this.functionChanged.emit();
        } finally {
          this.isSyncingProvider.set(false);
        }
      },
    });
  }

  onProviderSearch(event: {query: string}): void {
    this.currentProviderQuery.set(event.query);
    this.providerSearchSubject.next(event.query);
  }

  onProviderDropdownClick(): void {
    this.systemRecordingStateService.searchProviders(this.currentProviderQuery());
    this.scrollSelectedProviderIntoView();
  }

  private scrollSelectedProviderIntoView(): void {
    const providerId = this.currentFunctionEntry()?.providerId;
    if (!providerId) return;

    setTimeout(() => {
      const selectedOption = document.querySelector(
        `.p-autocomplete-overlay [data-item-id="${providerId}"]`,
      );
      selectedOption?.scrollIntoView({block: 'nearest'});
    });
  }

  onToolSearch(event: {query: string}): void {
    this.currentToolQuery.set(event.query);
    const currentEntry = this.currentFunctionEntry();
    this.toolSearchSubject.next({query: event.query, providerId: currentEntry?.providerId ?? null});
  }

  onToolDropdownClick(): void {
    const currentEntry = this.currentFunctionEntry();
    this.systemRecordingStateService.searchTools(
      this.currentToolQuery(),
      currentEntry?.providerId ?? null,
    );
  }

  async onAddNewProvider(): Promise<void> {
    this.isSyncingProvider.set(true);

    try {
      const provider = await this.systemRecordingStateService.createNewProvider(
        this.currentProviderQuery(),
      );

      if (provider?.wasCreated) {
        this.onProviderChange(provider);
        this.providerAutocomplete()?.hide();

        await this.toastService.showSuccess('common.success', 'common.create.success.detail');
      } else {
        await this.toastService.showError('common.error', 'errors.entityCreateFailed');
      }
    } finally {
      this.isSyncingProvider.set(false);
    }
  }

  async onAddNewTool(): Promise<void> {
    const currentEntry = this.currentFunctionEntry();
    if (!currentEntry?.providerId) return;

    this.isSyncingProvider.set(true);

    try {
      const tool = await this.systemRecordingStateService.createNewTool(
        this.currentToolQuery(),
        currentEntry.providerId,
      );

      if (tool) {
        this.onToolChange(tool);
        this.toolAutocomplete()?.hide();

        await this.toastService.showSuccess('common.success', 'common.create.success.detail');
      } else {
        await this.toastService.showError('common.error', 'errors.entityCreateFailed');
      }
    } finally {
      this.isSyncingProvider.set(false);
    }
  }

  async onDeleteProvider(provider: ProviderData): Promise<void> {
    this.confirmDialogService.confirm({
      headerKey: 'project.steps.systemRecording.confirmDeleteProvider.header',
      messageKey: 'project.steps.systemRecording.confirmDeleteProvider.message',
      accept: async () => {
        await this.handleDelete(
          () => this.systemRecordingStateService.deleteProvider(provider.providerId),
          this.providerAutocomplete(),
          'project.steps.systemRecording.providers.deletedDetail',
          'project.steps.systemRecording.providers.inUseError',
        );
      },
    });
  }

  async onDeleteTool(tool: ToolData): Promise<void> {
    const currentEntry = this.currentFunctionEntry();
    const providerId = currentEntry?.providerId;
    if (!providerId) return;

    this.confirmDialogService.confirm({
      headerKey: 'project.steps.systemRecording.confirmDeleteTool.header',
      messageKey: 'project.steps.systemRecording.confirmDeleteTool.message',
      accept: async () => {
        await this.handleDelete(
          () => this.systemRecordingStateService.deleteTool(tool.toolId, providerId),
          this.toolAutocomplete(),
          'project.steps.systemRecording.tools.deletedDetail',
          'project.steps.systemRecording.tools.inUseError',
        );
      },
    });
  }

  isCustomProvider = (provider: ProviderData): boolean => {
    return provider.tools.length === 0 || provider.tools.every((tool) => !tool.directusId);
  };

  isCustomTool = (tool: ToolData): boolean => {
    return !tool.directusId;
  };

  private async handleDelete(
    deleteOperation: () => Promise<boolean>,
    autocomplete: ModelFieldAutocompleteComponent | undefined,
    successMessageKey: string,
    inUseMessageKey: string,
  ): Promise<void> {
    this.isSyncingProvider.set(true);

    try {
      const success = await deleteOperation();

      if (success) {
        autocomplete?.hide();
        await this.toastService.showSuccess('common.success', successMessageKey);
      } else {
        await this.toastService.showError('common.error', inUseMessageKey);
      }
    } finally {
      this.isSyncingProvider.set(false);
    }
  }

  private clearToolSelection(): void {
    this.currentToolQuery.set('');
    this.toolAutocomplete()?.clear();
  }

  private clearProviderAndToolSelection(): void {
    this.currentProviderQuery.set('');
    this.providerAutocomplete()?.clear();
    this.clearToolSelection();
  }
}
